import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { gradesSchoolYears, modals, schoolYears, users } from "../../ducks";
import {currentCustomer, duckify, filterDuck, singleDuck} from "../../utils/duckify";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import SchoolYearConfigPage from "./SchoolYearConfigPage";
import { FormCheck } from 'react-bootstrap'
import CollectionStatus from "./CollectionStatus";
import LinkButton from "../LinkButton/LinkButton";

import { faCaretDown } from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons/faCaretRight";
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons/faInfoCircle";
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons/faTrashAlt'
import { connect } from "react-redux";
import {titleize} from "../../utils";

const SchoolYearTable = ({ currentUser, dispatch, schoolYears, customer, ...props }) => {
  const appTerminology = titleize(customer && customer.admin_admissions_terms['noun']);

  return (
    <div className="school-year-table">
      <table className="table">
        <thead>
          <tr>
            <th></th>
            <th>Name</th>
            <th className="terms-table-header">
              Start Year
              <OverlayTrigger
                placement={'top'}
                overlay={
                  <Tooltip>
                    The year it will be when the term begins.
                  </Tooltip>
                }
              >
                <span>
                  <FontAwesomeIcon className="tooltip-icon" icon={faInfoCircle} />
                </span>
              </OverlayTrigger>
            </th>
            <th className="terms-table-header">
              Available on Inquiry Forms
              <OverlayTrigger
                placement={'top'}
                overlay={
                  <Tooltip>
                    Choose 'Yes' to make the term an option for Apply Year on Inquiry forms.
                  </Tooltip>
                }
              >
                <span>
                  <FontAwesomeIcon className="tooltip-icon" icon={faInfoCircle} />
                </span>
              </OverlayTrigger>
            </th>
            <th className="terms-table-header">
              Available on Other Forms
              <OverlayTrigger
                placement={'top'}
                overlay={
                  <Tooltip>
                    Choose 'Yes' to make the term an option for all other forms.
                  </Tooltip>
                }
              >
                <span>
                  <FontAwesomeIcon className="tooltip-icon" icon={faInfoCircle} />
                </span>
              </OverlayTrigger>
            </th>
            <th>{appTerminology} Collection Status</th>
            {currentUser.acl >= 3 &&
              <th className="actions terms-table-header">
                Actions
                <OverlayTrigger
                  placement={'top'}
                  overlay={
                    <Tooltip>
                      Bulk Action Audit Log
                    </Tooltip>
                  }
                >
                  <span>
                    <LinkButton
                      id="bulk-action-event"
                      className="tooltip-icon"
                      onClick={() => dispatch(modals.actions.show('BulkActionEventModal', { name: 'data_retention' }))}
                      content={
                        <FontAwesomeIcon icon={faBook} className="hover-button" />
                      }
                    />
                  </span>
                </OverlayTrigger>
              </th>
            }
            <th>Edit History</th>
          </tr>
        </thead>
        <tbody>
          {schoolYears.map(schoolYear => (
            <SchoolYearRow key={schoolYear.id} schoolYear={schoolYear} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

const SchoolYearRow = connect()(({ dispatch, schoolYear, ...props }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [localSchoolYear, setLocalSchoolYear] = useState(schoolYear) // To prevent incorrect collection status flashing
  const toggleRow = () => setIsExpanded(!isExpanded)

  useEffect(() => {
    // We only want to update if we are getting collection attributes.
    if(schoolYear.collecting_applications !== undefined) {
      setLocalSchoolYear(schoolYear)
    }
  }, [schoolYear])

  const dispatchChanges = schoolYear => {
    dispatch(schoolYears.actions.save(schoolYear))
      .then(() => dispatch(schoolYears.actions.markDirty()))
      .then(() => dispatch(gradesSchoolYears.actions.markDirty()))
  }

  const toggleAvailableInquiry = () => {
    const updatedSchoolYear = {
      ...schoolYear,
      valid_inquiry_year: !schoolYear.valid_inquiry_year
    }
    dispatchChanges(updatedSchoolYear)
  }

  const toggleAvailableApplication = () => {
    const updatedSchoolYear = {
      ...schoolYear,
      valid_apply_year: !schoolYear.valid_apply_year
    }
    dispatchChanges(updatedSchoolYear)
  }

  return (
    <>
      <tr className={`main-row ${isExpanded ? 'selected' : ''}`} data-id={schoolYear.id}>
        <td onClick={toggleRow} className="expand-button">
          {isExpanded ? <FontAwesomeIcon icon={faCaretDown} /> : <FontAwesomeIcon icon={faCaretRight} />}
        </td>
        <td>{schoolYear.name}</td>
        <td>{schoolYear.start_year}</td>
        <td className="form-toggle-switch">
          <FormCheck
            type="switch"
            className="valid-inquiry-year"
            label={schoolYear.valid_inquiry_year ? 'Yes' : 'No'}
            value={schoolYear.valid_inquiry_year}
            checked={schoolYear.valid_inquiry_year}
            onChange={toggleAvailableInquiry}
          />
        </td>
        <td className="form-toggle-switch">
          <FormCheck
            type="switch"
            className="valid-apply-year"
            label={schoolYear.valid_apply_year ? 'Yes' : 'No'}
            value={schoolYear.valid_apply_year}
            checked={schoolYear.valid_apply_year}
            onChange={toggleAvailableApplication}
          />
        </td>
        <td>
          <CollectionStatus
            isCollected={localSchoolYear.collecting_applications}
            reasons={localSchoolYear.reasons}
          />
        </td>
        <td className="actions">
          <a href={`/setup/school_years/${schoolYear.id}/edit`} className="open-dialog btn btn-outline-primary btn-sm">
            Edit
          </a>
          <span className="data-retention">
            <OverlayTrigger
              placement={'top'}
              popperConfig={{
                modifiers: [{
                  name: 'offset',
                  enabled: true,
                  options: {
                    offset: [0, 10]
                  }
                }]
              }}
              overlay={
                <Tooltip>
                  Clear Review Data
                </Tooltip>
              }
            >
              <span>
                <LinkButton
                  id={`${schoolYear.start_year}-data-retention`}
                  onClick={() => dispatch(modals.actions.show('DataRetention', { schoolYear }))}
                  content={
                    <FontAwesomeIcon icon={faTrashAlt} className="fa-lg hover-button red" />
                  }
                />
              </span>
            </OverlayTrigger>
          </span>
        </td>
        <td className="latest_audit" >
          <LinkButton
            onClick={() => dispatch(modals.actions.show('AuditViewer', { id: schoolYear.id, resource: 'school_years' }))}
            content={'View Changes'}
          />
        </td>

      </tr>
      {isExpanded &&
        <tr >
          <td colSpan={8}>
            <SchoolYearConfigPage schoolYear={schoolYear} />
          </td>
        </tr>
      }
    </>
  )
})

const mapDucksToProps = () => {
  return ({
    currentUser: singleDuck(users, 'current'),
    schoolYears: filterDuck(schoolYears, { include_collection_attrs: true }),
    customer: currentCustomer
  })
}

export default compose(
  duckify(mapDucksToProps, () => ({}), () => ({}))
)(SchoolYearTable)
